import { useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import {
  useAddClusterMember,
  useRemoveClusterMember,
  useSelectableInternalUsers,
} from "../api/clusters";
import type { ClusterMember, DocumentationCluster } from "../types";

interface Props {
  cluster: DocumentationCluster;
  onClose: () => void;
}

function memberLabel(u: { first_name: string; last_name: string; username: string }) {
  const full = `${u.first_name} ${u.last_name}`.trim();
  return full || u.username;
}

export function ClusterMembersDialog({ cluster, onClose }: Props) {
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);

  const addMember = useAddClusterMember(cluster.id);
  const removeMember = useRemoveClusterMember(cluster.id);
  // Only fetched once the add panel is open — the roster itself comes with the cluster.
  const allUsers = useSelectableInternalUsers(adding);

  const existingIds = useMemo(
    () => new Set(cluster.members.map((m) => m.user_id)),
    [cluster.members],
  );

  const candidates = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (allUsers.data ?? [])
      .filter((u) => !existingIds.has(u.id))
      .filter((u) => !term || memberLabel(u).toLowerCase().includes(term))
      .sort((a, b) => memberLabel(a).localeCompare(memberLabel(b), "pl"));
  }, [allUsers.data, existingIds, search]);

  function reportError(err: unknown, fallback: string) {
    const apiErr = (err as { response?: { data?: { errors?: unknown } } })
      ?.response?.data?.errors;
    setError(typeof apiErr === "string" ? apiErr : fallback);
  }

  async function handleAdd(userId: string) {
    setError(null);
    try {
      await addMember.mutateAsync(userId);
      setSearch("");
    } catch (err) {
      reportError(err, "Nie udało się dodać pracownika. Spróbuj ponownie.");
    }
  }

  async function handleRemove(member: ClusterMember) {
    setError(null);
    try {
      await removeMember.mutateAsync(member.user_id);
    } catch (err) {
      reportError(err, "Nie udało się usunąć pracownika. Spróbuj ponownie.");
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div
        role="dialog"
        aria-label="Członkowie klastra"
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg p-6"
      >
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Członkowie klastra
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Dodani pracownicy widzą ten klaster na swojej liście i mogą dodawać do
          niego dokumenty. Autor klastra, koordynatorzy przedsięwzięcia,
          dyrektorzy i CEO mają dostęp automatycznie — nie trzeba ich dodawać.
        </p>

        <div className="mb-4">
          {cluster.members.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic py-2">
              Brak dodanych pracowników.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {cluster.members.map((m) => (
                <li key={m.id} className="flex items-center justify-between py-2">
                  <div className="min-w-0">
                    <p className="text-sm text-gray-900 dark:text-gray-100 truncate">
                      {m.full_name}
                    </p>
                    {m.added_by_name && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        Dodany przez: {m.added_by_name}
                      </p>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => handleRemove(m)}
                    disabled={removeMember.isPending}
                  >
                    Usuń
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {adding ? (
          <div className="mb-4">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Szukaj pracownika…"
              autoFocus
              className="w-full rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-700 dark:text-gray-300 px-3 py-2 mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="max-h-56 overflow-y-auto rounded-md border border-gray-100 dark:border-gray-700">
              {allUsers.isPending ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 p-3">
                  Ładowanie…
                </p>
              ) : candidates.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 p-3">
                  Brak pasujących pracowników.
                </p>
              ) : (
                candidates.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => handleAdd(u.id)}
                    disabled={addMember.isPending}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                  >
                    {memberLabel(u)}
                  </button>
                ))
              )}
            </div>
          </div>
        ) : null}

        {error && <p className="text-sm text-red-600 dark:text-red-400 mb-2">{error}</p>}

        <div className="flex justify-between gap-2 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setAdding((v) => !v);
              setSearch("");
              setError(null);
            }}
          >
            {adding ? "Zakończ dodawanie" : "Dodaj pracownika"}
          </Button>
          <Button type="button" onClick={onClose}>
            Zamknij
          </Button>
        </div>
      </div>
    </div>
  );
}
